import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PenIcon } from "assets/icons";
import { Button, AlertMessage, CheckBox } from "components/atoms";
import {
  TextInput,
  MessageTextAreaInput,
  GeneralSelectInput,
  TabbedFormToggle,
  TabConfig,
  EditTabConfig,
  DatePickerInput,
  RadioButtonGroup,
  InformationItem,
} from "components/molecules";
import { useUserData, useContentSanitizer } from "hooks";
import { ProfileHandling, formatDate, formatDateToString } from "services";
import { gradeOptions, countriesFlat } from "types";

const academicHistorySchema = Yup.object({
  completedYear12InAustralia: Yup.boolean().required("Please select yes or no"),
  atarScore: Yup.number().when("completedYear12InAustralia", {
    is: true,
    then: (schema) => schema.required("Please provide an ATAR score"),
    otherwise: (schema) => schema.nullable(),
  }),
  hasBeenExcluded: Yup.boolean().required("Please select yes or no"),
  exclusionDetails: Yup.string().when("hasBeenExcluded", {
    is: true,
    then: (schema) => schema.required("Please provide exclusion details"),
    otherwise: (schema) => schema.nullable(),
  }),
  usiNumber: Yup.string().nullable(),
  highSchoolName: Yup.string().required("Please enter your high school name"),
  highSchoolCity: Yup.string().required("Please enter city"),
  highSchoolCountry: Yup.string().required("Please enter country"),
  highSchoolGrade: Yup.string().required("Please enter a grade"),
  highSchoolStartDate: Yup.date().required("Please provide a start date"),
  highSchoolEndDate: Yup.date().when("highSchoolIsStillAttending", {
    is: false,
    then: (schema) => schema.required("Please provide an end date"),
    otherwise: (schema) => schema.nullable(),
  }),
  tertiary1School: Yup.string().required("Please enter institution name"),
  tertiary1Course: Yup.string().required("Please enter course name"),
  tertiary1City: Yup.string().required("Please enter city"),
  tertiary1Country: Yup.string().required("Please enter country"),
  tertiary1Grade: Yup.string().required("Please enter a grade"),
  tertiary1EndDate: Yup.date().when("tertiary1IsStillAttending", {
    is: false,
    then: (schema) => schema.required("Please provide an end date"),
    otherwise: (schema) => schema.nullable(),
  }),

  tertiary2School: Yup.string().test(
    "conditional-required",
    "Please enter institution name",
    function (value) {
      const {
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2Course: Yup.string().test(
    "conditional-required",
    "Please enter course name",
    function (value) {
      const {
        tertiary2School,
        tertiary2City,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2City,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2City: Yup.string().test(
    "conditional-required",
    "Please enter city",
    function (value) {
      const {
        tertiary2School,
        tertiary2Course,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2Course,
        tertiary2Country,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2Country: Yup.string().test(
    "conditional-required",
    "Please enter country",
    function (value) {
      const {
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Grade,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2Grade: Yup.string().test(
    "conditional-required",
    "Please enter a grade",
    function (value) {
      const {
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2StartDate: Yup.string().test(
    "conditional-required",
    "Please provide a start date",
    function (value) {
      const {
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary2EndDate: Yup.string().test(
    "conditional-required",
    "Please provide an end date",
    function (value) {
      const {
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary2School,
        tertiary2Course,
        tertiary2City,
        tertiary2Country,
        tertiary2StartDate,
        tertiary2EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),

  tertiary3School: Yup.string().test(
    "conditional-required",
    "Please enter institution name",
    function (value) {
      const {
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3Course: Yup.string().test(
    "conditional-required",
    "Please enter course name",
    function (value) {
      const {
        tertiary3School,
        tertiary3City,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3City,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3City: Yup.string().test(
    "conditional-required",
    "Please enter city",
    function (value) {
      const {
        tertiary3School,
        tertiary3Course,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3Course,
        tertiary3Country,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3Country: Yup.string().test(
    "conditional-required",
    "Please enter country",
    function (value) {
      const {
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Grade,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3Grade: Yup.string().test(
    "conditional-required",
    "Please enter a grade",
    function (value) {
      const {
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3StartDate: Yup.string().test(
    "conditional-required",
    "Please provide a start date",
    function (value) {
      const {
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  tertiary3EndDate: Yup.string().test(
    "conditional-required",
    "Please provide an end date",
    function (value) {
      const {
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      } = this.parent;
      const hasAnyValue = [
        tertiary3School,
        tertiary3Course,
        tertiary3City,
        tertiary3Country,
        tertiary3StartDate,
        tertiary3EndDate,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),

  currentlyStudyingAt: Yup.string().nullable(),
});

const AcademicHistoryPage: React.FC = () => {
  const { userData, fetchUserData } = useUserData();
  const { sanitizeContent } = useContentSanitizer();
  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      completedYear12InAustralia: null as boolean | null,
      atarScore: null as number | null,
      hasBeenExcluded: null as boolean | null,
      exclusionDetails: "",
      usiNumber: "",
      highSchoolName: "",
      highSchoolCity: "",
      highSchoolCountry: "",
      highSchoolGrade: "",
      highSchoolIsStillAttending: false,
      highSchoolStartDate: null as Date | null,
      highSchoolEndDate: null as Date | null,
      tertiary1School: "",
      tertiary1Course: "",
      tertiary1City: "",
      tertiary1Country: "",
      tertiary1Grade: "",
      tertiary1IsStillAttending: false,
      tertiary1StartDate: null as Date | null,
      tertiary1EndDate: null as Date | null,
      tertiary2School: "",
      tertiary2Course: "",
      tertiary2City: "",
      tertiary2Country: "",
      tertiary2Grade: "",
      tertiary2IsStillAttending: false,
      tertiary2StartDate: null as Date | null,
      tertiary2EndDate: null as Date | null,
      tertiary3School: "",
      tertiary3Course: "",
      tertiary3City: "",
      tertiary3Country: "",
      tertiary3Grade: "",
      tertiary3IsStillAttending: false,
      tertiary3StartDate: null as Date | null,
      tertiary3EndDate: null as Date | null,
      currentlyStudyingAt: "",
    },
    validationSchema: academicHistorySchema,
    onSubmit: () => {},
  });

  const resetFormToUserData = () => {
    if (!userData) return;

    const tertiaryData = [
      userData?.tertiaryEducation1,
      userData?.tertiaryEducation2,
      userData?.tertiaryEducation3,
    ];

    formik.setValues({
      completedYear12InAustralia:
        userData?.educationDetails?.completedYear12InAustralia ?? null,
      atarScore: userData?.educationDetails?.atarScore || null,
      hasBeenExcluded: userData?.educationDetails?.hasBeenExcluded ?? null,
      exclusionDetails: userData?.educationDetails?.exclusionDetails || "",
      usiNumber: userData?.educationDetails?.usiNumber || "",
      highSchoolName: userData?.highSchool?.school || "",
      highSchoolCity: userData?.highSchool?.city || "",
      highSchoolCountry: userData?.highSchool?.country || "",
      highSchoolGrade: userData?.highSchool?.gradeObtained || "",
      highSchoolIsStillAttending:
        userData?.highSchool?.isStillAttending || false,
      highSchoolStartDate: userData?.highSchool?.startDate
        ? new Date(userData.highSchool.startDate)
        : null,
      highSchoolEndDate: userData?.highSchool?.endDate
        ? new Date(userData.highSchool.endDate)
        : null,

      tertiary1School: tertiaryData[0]?.school || "",
      tertiary1Course: tertiaryData[0]?.course || "",
      tertiary1City: tertiaryData[0]?.city || "",
      tertiary1Country: tertiaryData[0]?.country || "",
      tertiary1Grade: tertiaryData[0]?.gradeObtained || "",
      tertiary1IsStillAttending: tertiaryData[0]?.isStillAttending || false,
      tertiary1StartDate: tertiaryData[0]?.startDate
        ? new Date(tertiaryData[0].startDate)
        : null,
      tertiary1EndDate: tertiaryData[0]?.endDate
        ? new Date(tertiaryData[0].endDate)
        : null,

      tertiary2School: tertiaryData[1]?.school || "",
      tertiary2Course: tertiaryData[1]?.course || "",
      tertiary2City: tertiaryData[1]?.city || "",
      tertiary2Country: tertiaryData[1]?.country || "",
      tertiary2Grade: tertiaryData[1]?.gradeObtained || "",
      tertiary2IsStillAttending: tertiaryData[1]?.isStillAttending || false,
      tertiary2StartDate: tertiaryData[1]?.startDate
        ? new Date(tertiaryData[1].startDate)
        : null,
      tertiary2EndDate: tertiaryData[1]?.endDate
        ? new Date(tertiaryData[1].endDate)
        : null,

      tertiary3School: tertiaryData[2]?.school || "",
      tertiary3Course: tertiaryData[2]?.course || "",
      tertiary3City: tertiaryData[2]?.city || "",
      tertiary3Country: tertiaryData[2]?.country || "",
      tertiary3Grade: tertiaryData[2]?.gradeObtained || "",
      tertiary3IsStillAttending: tertiaryData[2]?.isStillAttending || false,
      tertiary3StartDate: tertiaryData[2]?.startDate
        ? new Date(tertiaryData[2].startDate)
        : null,
      tertiary3EndDate: tertiaryData[2]?.endDate
        ? new Date(tertiaryData[2].endDate)
        : null,

      currentlyStudyingAt: "",
    });
  };

  const getFormValue = (key: string) =>
    formik.values[key as keyof typeof formik.values];

  const getCompletionYear = (endDate: Date | null) => {
    if (endDate) return endDate.getFullYear();
    return null;
  };

  const calculateDuration = (startDate: Date | null, endDate: Date | null) => {
    if (startDate && endDate) {
      const years = endDate.getFullYear() - startDate.getFullYear();
      return Math.max(1, years);
    }
    return null;
  };

  const isHighSchoolComplete = () => {
    return !!(
      getFormValue("highSchoolName") &&
      getFormValue("highSchoolCity") &&
      getFormValue("highSchoolCountry") &&
      getFormValue("highSchoolGrade") &&
      getFormValue("highSchoolStartDate") &&
      getFormValue("highSchoolEndDate")
    );
  };

  const isTertiaryComplete = (level: number) => {
    const prefix = `tertiary${level}`;
    return !!(
      getFormValue(`${prefix}School`) &&
      getFormValue(`${prefix}Course`) &&
      getFormValue(`${prefix}City`) &&
      getFormValue(`${prefix}Country`) &&
      getFormValue(`${prefix}Grade`) &&
      getFormValue(`${prefix}StartDate`) &&
      getFormValue(`${prefix}EndDate`)
    );
  };

  const shouldShowTertiary1 = () => {
    const hasExistingData =
      userData?.tertiaryEducation1 &&
      (userData.tertiaryEducation1.school ||
        userData.tertiaryEducation1.course ||
        userData.tertiaryEducation1.city ||
        userData.tertiaryEducation1.country ||
        userData.tertiaryEducation1.gradeObtained ||
        userData.tertiaryEducation1.startDate ||
        userData.tertiaryEducation1.endDate);

    return (
      hasExistingData ||
      (isHighSchoolComplete() && !getFormValue("highSchoolIsStillAttending"))
    );
  };

  const shouldShowTertiary2 = () => {
    const hasExistingData =
      userData?.tertiaryEducation2 &&
      (userData.tertiaryEducation2.school ||
        userData.tertiaryEducation2.course ||
        userData.tertiaryEducation2.city ||
        userData.tertiaryEducation2.country ||
        userData.tertiaryEducation2.gradeObtained ||
        userData.tertiaryEducation2.startDate ||
        userData.tertiaryEducation2.endDate);

    const tertiary1HasAnyData = shouldShowTertiary1();

    return hasExistingData || tertiary1HasAnyData;
  };

  const shouldShowTertiary3 = () => {
    const hasExistingData =
      userData?.tertiaryEducation3 &&
      (userData.tertiaryEducation3.school ||
        userData.tertiaryEducation3.course ||
        userData.tertiaryEducation3.city ||
        userData.tertiaryEducation3.country ||
        userData.tertiaryEducation3.gradeObtained ||
        userData.tertiaryEducation3.startDate ||
        userData.tertiaryEducation3.endDate);

    const tertiary2HasAnyData = shouldShowTertiary2();

    return hasExistingData || tertiary2HasAnyData;
  };

  const isOverviewComplete = () => {
    const hasBeenExcluded = formik.values.hasBeenExcluded;

    if (hasBeenExcluded === null || hasBeenExcluded === undefined) {
      return false;
    }

    if (hasBeenExcluded === true) {
      return !!getFormValue("exclusionDetails");
    }
    return true;
  };

  const isHighSchoolSectionComplete = () => {
    const isBasicComplete = isHighSchoolComplete();
    if (!getFormValue("highSchoolIsStillAttending")) {
      return isBasicComplete && !!getFormValue("highSchoolEndDate");
    }
    return isBasicComplete;
  };

  const isTertiarySectionComplete = (level: number) => {
    const prefix = `tertiary${level}`;
    const isBasicComplete = isTertiaryComplete(level);
    if (level === 1 && !getFormValue(`${prefix}IsStillAttending`)) {
      return isBasicComplete && !!getFormValue(`${prefix}EndDate`);
    }
    return isBasicComplete;
  };

  const handleCurrentlyStudyingChange = async (
    field: string,
    checked: boolean
  ) => {
    if (checked) {
      if (field === "highSchoolIsStillAttending") {
        formik.setFieldValue("highSchoolIsStillAttending", true);
        formik.setFieldValue("tertiary1IsStillAttending", false);
        formik.setFieldValue("highSchoolEndDate", null);
      } else if (field === "tertiary1IsStillAttending") {
        formik.setFieldValue("highSchoolIsStillAttending", false);
        formik.setFieldValue("tertiary1IsStillAttending", true);
        formik.setFieldValue("tertiary1EndDate", null);
      }

      if (
        field === "highSchoolIsStillAttending" &&
        editingTab !== "highSchool"
      ) {
        await saveHighSchool();
      } else if (
        field === "tertiary1IsStillAttending" &&
        editingTab !== "tertiary1"
      ) {
        await saveTertiary(1)();
      }
    } else {
      formik.setFieldValue(field, false);
    }
  };

  const validateFields = async (fields: string[]): Promise<boolean> => {
    const allErrors = await formik.validateForm();
    const relevantErrors = Object.keys(allErrors).filter((key) =>
      fields.includes(key)
    );
    if (relevantErrors.length > 0) {
      const touchedFields = fields.reduce(
        (acc: { [key: string]: boolean }, field: string) => ({
          ...acc,
          [field]: true,
        }),
        {}
      );
      formik.setTouched(touchedFields);
      setErrorMessage("Please fix the validation errors before saving.");
      return false;
    }
    return true;
  };

  const saveOverview = async (): Promise<boolean> => {
    try {
      const fields = ["hasBeenExcluded", "exclusionDetails"];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        educationDetails: {
          completedYear12InAustralia: formik.values.completedYear12InAustralia,
          atarScore: getFormValue("atarScore"),
          hasBeenExcluded: formik.values.hasBeenExcluded,
          exclusionDetails: sanitizeContent(
            getFormValue("exclusionDetails") as string
          ),
          usiNumber: sanitizeContent(getFormValue("usiNumber") as string),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Education overview updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update education overview.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveHighSchool = async (): Promise<boolean> => {
    try {
      const fields = [
        "highSchoolName",
        "highSchoolCity",
        "highSchoolCountry",
        "highSchoolGrade",
        "highSchoolEndDate",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        highSchool: {
          school: sanitizeContent(getFormValue("highSchoolName") as string),
          city: sanitizeContent(getFormValue("highSchoolCity") as string),
          country: sanitizeContent(getFormValue("highSchoolCountry") as string),
          gradeObtained: sanitizeContent(
            getFormValue("highSchoolGrade") as string
          ),
          yearOfCompletion: getCompletionYear(
            getFormValue("highSchoolEndDate") as Date
          ),
          isStillAttending: getFormValue("highSchoolIsStillAttending"),
          startDate: getFormValue("highSchoolStartDate")
            ? formatDate(getFormValue("highSchoolStartDate") as Date)
            : null,
          endDate: getFormValue("highSchoolEndDate")
            ? formatDate(getFormValue("highSchoolEndDate") as Date)
            : null,
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("High school details updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update high school details.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveTertiary = (level: number) => async (): Promise<boolean> => {
    try {
      const prefix = `tertiary${level}`;
      const fields = [
        `${prefix}School`,
        `${prefix}Course`,
        `${prefix}City`,
        `${prefix}Country`,
        `${prefix}Grade`,
        `${prefix}EndDate`,
      ];
      if (!(await validateFields(fields))) return false;

      const startDate = getFormValue(`${prefix}StartDate`) as Date | null;
      const endDate = getFormValue(`${prefix}EndDate`) as Date | null;

      const input = {
        id: userData!.id,
        [`tertiaryEducation${level}`]: {
          school: sanitizeContent(getFormValue(`${prefix}School`) as string),
          course: sanitizeContent(getFormValue(`${prefix}Course`) as string),
          duration: calculateDuration(startDate, endDate),
          city: sanitizeContent(getFormValue(`${prefix}City`) as string),
          country: sanitizeContent(getFormValue(`${prefix}Country`) as string),
          gradeObtained: sanitizeContent(
            getFormValue(`${prefix}Grade`) as string
          ),
          yearOfCompletion: getCompletionYear(
            getFormValue(`${prefix}EndDate`) as Date
          ),
          isStillAttending: getFormValue(`${prefix}IsStillAttending`),
          startDate: getFormValue(`${prefix}StartDate`)
            ? formatDate(getFormValue(`${prefix}StartDate`) as Date)
            : null,
          endDate: getFormValue(`${prefix}EndDate`)
            ? formatDate(getFormValue(`${prefix}EndDate`) as Date)
            : null,
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage(
        `${level === 1 ? "Latest" : level === 2 ? "2nd" : "3rd"} tertiary education updated successfully.`
      );
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage(
        `Failed to update ${level === 1 ? "latest" : level === 2 ? "2nd" : "3rd"} tertiary education.`
      );
      setSuccessMessage("");
      return false;
    }
  };

  useEffect(() => {
    if (!userData) return;

    const tertiaryData = [
      userData?.tertiaryEducation1,
      userData?.tertiaryEducation2,
      userData?.tertiaryEducation3,
    ];

    formik.setValues({
      completedYear12InAustralia:
        userData?.educationDetails?.completedYear12InAustralia ?? null,
      atarScore: userData?.educationDetails?.atarScore || null,
      hasBeenExcluded: userData?.educationDetails?.hasBeenExcluded ?? null,
      exclusionDetails: userData?.educationDetails?.exclusionDetails || "",
      usiNumber: userData?.educationDetails?.usiNumber || "",
      highSchoolName: userData?.highSchool?.school || "",
      highSchoolCity: userData?.highSchool?.city || "",
      highSchoolCountry: userData?.highSchool?.country || "",
      highSchoolGrade: userData?.highSchool?.gradeObtained || "",
      highSchoolIsStillAttending:
        userData?.highSchool?.isStillAttending || false,
      highSchoolStartDate: userData?.highSchool?.startDate
        ? new Date(userData.highSchool.startDate)
        : null,
      highSchoolEndDate: userData?.highSchool?.endDate
        ? new Date(userData.highSchool.endDate)
        : null,

      // Tertiary 1
      tertiary1School: tertiaryData[0]?.school || "",
      tertiary1Course: tertiaryData[0]?.course || "",
      tertiary1City: tertiaryData[0]?.city || "",
      tertiary1Country: tertiaryData[0]?.country || "",
      tertiary1Grade: tertiaryData[0]?.gradeObtained || "",
      tertiary1IsStillAttending: tertiaryData[0]?.isStillAttending || false,
      tertiary1StartDate: tertiaryData[0]?.startDate
        ? new Date(tertiaryData[0].startDate)
        : null,
      tertiary1EndDate: tertiaryData[0]?.endDate
        ? new Date(tertiaryData[0].endDate)
        : null,

      // Tertiary 2
      tertiary2School: tertiaryData[1]?.school || "",
      tertiary2Course: tertiaryData[1]?.course || "",
      tertiary2City: tertiaryData[1]?.city || "",
      tertiary2Country: tertiaryData[1]?.country || "",
      tertiary2Grade: tertiaryData[1]?.gradeObtained || "",
      tertiary2IsStillAttending: tertiaryData[1]?.isStillAttending || false,
      tertiary2StartDate: tertiaryData[1]?.startDate
        ? new Date(tertiaryData[1].startDate)
        : null,
      tertiary2EndDate: tertiaryData[1]?.endDate
        ? new Date(tertiaryData[1].endDate)
        : null,

      // Tertiary 3
      tertiary3School: tertiaryData[2]?.school || "",
      tertiary3Course: tertiaryData[2]?.course || "",
      tertiary3City: tertiaryData[2]?.city || "",
      tertiary3Country: tertiaryData[2]?.country || "",
      tertiary3Grade: tertiaryData[2]?.gradeObtained || "",
      tertiary3IsStillAttending: tertiaryData[2]?.isStillAttending || false,
      tertiary3StartDate: tertiaryData[2]?.startDate
        ? new Date(tertiaryData[2].startDate)
        : null,
      tertiary3EndDate: tertiaryData[2]?.endDate
        ? new Date(tertiaryData[2].endDate)
        : null,

      currentlyStudyingAt: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const createTertiaryTab = (level: number) => {
    const prefix = `tertiary${level}`;
    const labels = ["Latest Tertiary", "2nd Tertiary", "3rd Tertiary"];
    const descriptions = [
      "Most recent higher education",
      "Previous higher education",
      "Earlier higher education",
    ];

    return {
      id: prefix,
      label: labels[level - 1],
      description: descriptions[level - 1],
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>{labels[level - 1]}</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab(prefix)}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Institution:"
              value={String(getFormValue(`${prefix}School`) || "Not Provided")}
            />
            <InformationItem
              label="Course/Degree:"
              value={String(getFormValue(`${prefix}Course`) || "Not Provided")}
            />
            <InformationItem
              label="City:"
              value={String(getFormValue(`${prefix}City`) || "Not Provided")}
            />
            <InformationItem
              label="Country:"
              value={String(getFormValue(`${prefix}Country`) || "Not Provided")}
            />
            <InformationItem
              label="Grade:"
              value={String(getFormValue(`${prefix}Grade`) || "Not Provided")}
            />
            <InformationItem
              label="Start Date:"
              value={
                getFormValue(`${prefix}StartDate`)
                  ? formatDateToString(
                      getFormValue(`${prefix}StartDate`) as Date
                    )
                  : "Not Provided"
              }
            />
            <InformationItem
              label="End Date:"
              value={
                getFormValue(`${prefix}EndDate`)
                  ? formatDateToString(getFormValue(`${prefix}EndDate`) as Date)
                  : "Not Provided"
              }
            />
            <InformationItem
              label="Currently Studying:"
              value={getFormValue(`${prefix}IsStillAttending`) ? "Yes" : "No"}
            />
          </dl>
        </div>
      ),
    };
  };

  const createTertiaryEditTab = (level: number) => {
    const prefix = `tertiary${level}`;
    const labels = ["Latest Tertiary", "2nd Tertiary", "3rd Tertiary"];

    return {
      id: prefix,
      label: labels[level - 1],
      title: `Edit ${labels[level - 1]}`,
      onSave: saveTertiary(level),
      saveDisabled: !isTertiarySectionComplete(level),
      formFields: (
        <>
          <TextInput
            id={`${prefix}School`}
            label="Institution Name *"
            placeholder="Enter institution name"
            getFieldProps={() => ({
              value: getFormValue(`${prefix}School`),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={
              formik.errors[`${prefix}School` as keyof typeof formik.errors]
            }
            touched={
              formik.touched[`${prefix}School` as keyof typeof formik.touched]
            }
          />
          <TextInput
            id={`${prefix}Course`}
            label="Course/Degree *"
            placeholder="Enter course or degree name"
            getFieldProps={() => ({
              value: getFormValue(`${prefix}Course`),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={
              formik.errors[`${prefix}Course` as keyof typeof formik.errors]
            }
            touched={
              formik.touched[`${prefix}Course` as keyof typeof formik.touched]
            }
          />
          <TextInput
            id={`${prefix}City`}
            label="City *"
            placeholder="Enter city"
            getFieldProps={() => ({
              value: getFormValue(`${prefix}City`),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors[`${prefix}City` as keyof typeof formik.errors]}
            touched={
              formik.touched[`${prefix}City` as keyof typeof formik.touched]
            }
          />
          <GeneralSelectInput
            id={`${prefix}Country`}
            label="Country *"
            options={countriesFlat}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={(formik.values as any)[`${prefix}Country`]}
            error={
              formik.errors[`${prefix}Country` as keyof typeof formik.errors]
            }
            touched={
              formik.touched[`${prefix}Country` as keyof typeof formik.touched]
            }
          />
          <GeneralSelectInput
            id={`${prefix}Grade`}
            label="Grade *"
            options={gradeOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={getFormValue(`${prefix}Grade`) as string}
            error={
              formik.errors[`${prefix}Grade` as keyof typeof formik.errors]
            }
            touched={
              formik.touched[`${prefix}Grade` as keyof typeof formik.touched]
            }
          />
          <DatePickerInput
            id={`${prefix}StartDate`}
            className={`${prefix}StartDate`}
            label="Start Date"
            value={getFormValue(`${prefix}StartDate`) as Date | null}
            onChange={(date) =>
              formik.setFieldValue(`${prefix}StartDate`, date)
            }
            preset="pastDate"
          />
          <DatePickerInput
            id={`${prefix}EndDate`}
            className={`${prefix}EndDate`}
            label="End Date"
            value={getFormValue(`${prefix}EndDate`) as Date | null}
            disabled={getFormValue(`${prefix}IsStillAttending`) as boolean}
            onChange={(date) => formik.setFieldValue(`${prefix}EndDate`, date)}
            preset="pastDate"
          />
          {level === 1 && (
            <CheckBox
              label="Currently studying"
              checked={getFormValue(`${prefix}IsStillAttending`) as boolean}
              disabled={
                !isTertiaryComplete(level) ||
                !!getFormValue("highSchoolIsStillAttending")
              }
              onChange={(checked) =>
                handleCurrentlyStudyingChange(
                  `${prefix}IsStillAttending`,
                  checked
                )
              }
            />
          )}
        </>
      ),
    };
  };

  const displayTabs: TabConfig[] = [
    {
      id: "overview",
      label: "Education Overview",
      description: "General education information",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Education Overview</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("overview")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Did you complete year 12 in Australia?"
              value={
                getFormValue("completedYear12InAustralia") === null
                  ? "Not Provided"
                  : getFormValue("completedYear12InAustralia")
                    ? "Yes"
                    : "No"
              }
            />
            {getFormValue("completedYear12InAustralia") && (
              <InformationItem
                label="ATAR Score:"
                value={String(getFormValue("atarScore") || "Not Provided")}
              />
            )}
            <InformationItem
              label="Excluded from a course/program and/or institution:"
              value={
                getFormValue("hasBeenExcluded") === null
                  ? "Not Provided"
                  : getFormValue("hasBeenExcluded")
                    ? "Yes"
                    : "No"
              }
            />
            {getFormValue("hasBeenExcluded") && (
              <InformationItem
                label="Exclusion Details:"
                value={String(
                  getFormValue("exclusionDetails") || "Not Provided"
                )}
              />
            )}
            <InformationItem
              label="USI Number:"
              value={String(getFormValue("usiNumber") || "Not Provided")}
            />
          </dl>
        </div>
      ),
    },

    {
      id: "highschool",
      label: "High School",
      description: "Secondary education",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>High School Education</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("highschool")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="School:"
              value={String(getFormValue("highSchoolName") || "Not Provided")}
            />
            <InformationItem
              label="City:"
              value={String(getFormValue("highSchoolCity") || "Not Provided")}
            />
            <InformationItem
              label="Country:"
              value={String(
                getFormValue("highSchoolCountry") || "Not Provided"
              )}
            />
            <InformationItem
              label="Grade:"
              value={String(getFormValue("highSchoolGrade") || "Not Provided")}
            />
            <InformationItem
              label="Start Date:"
              value={
                getFormValue("highSchoolStartDate")
                  ? formatDateToString(
                      getFormValue("highSchoolStartDate") as Date
                    )
                  : "Not Provided"
              }
            />
            <InformationItem
              label="End Date:"
              value={
                getFormValue("highSchoolEndDate")
                  ? formatDateToString(
                      getFormValue("highSchoolEndDate") as Date
                    )
                  : "Not Provided"
              }
            />
            <InformationItem
              label="Currently studying:"
              value={getFormValue("highSchoolIsStillAttending") ? "Yes" : "No"}
            />
          </dl>
        </div>
      ),
    },
    ...(shouldShowTertiary1() ? [createTertiaryTab(1)] : []),
    ...(shouldShowTertiary2() ? [createTertiaryTab(2)] : []),
    ...(shouldShowTertiary3() ? [createTertiaryTab(3)] : []),
  ];

  const editTabConfigs: EditTabConfig[] = [
    {
      id: "overview",
      label: "Education Overview",
      title: "Edit Education Overview",
      onSave: saveOverview,
      onCancel: resetFormToUserData,
      saveDisabled: !isOverviewComplete(),
      formFields: (
        <>
          <RadioButtonGroup
            name="completedYear12InAustralia"
            label="Did you complete year 12 in Australia? *"
            value={formik.values.completedYear12InAustralia}
            onChange={(value) =>
              formik.setFieldValue("completedYear12InAustralia", value)
            }
          />
          {getFormValue("completedYear12InAustralia") && (
            <>
              <TextInput
                id="atarScore"
                label="University Admission Score (ATAR/UAI/OP) *"
                placeholder="Enter university admission score"
                getFieldProps={() => ({
                  value: getFormValue("atarScore"),
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                })}
                error={formik.errors.atarScore}
                touched={formik.touched.atarScore}
              />
            </>
          )}
          <RadioButtonGroup
            name="hasBeenExcluded"
            label="Excluded from a course/program and/or institution *"
            value={formik.values.hasBeenExcluded}
            onChange={(value) => formik.setFieldValue("hasBeenExcluded", value)}
          />
          {getFormValue("hasBeenExcluded") && (
            <MessageTextAreaInput
              id="exclusionDetails"
              label="Exclusion Details *"
              placeholder="Please provide details about the exclusion (e.g., reason for exclusion, institution name, dates, circumstances)"
              rows="4"
              maxLength={500}
              getFieldProps={() => ({
                value: getFormValue("exclusionDetails"),
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              })}
              error={formik.errors.exclusionDetails}
              touched={formik.touched.exclusionDetails}
            />
          )}
          <TextInput
            id="usiNumber"
            label="USI Number (for Australian students)"
            placeholder="Enter your Unique Student Identifier"
            getFieldProps={() => ({
              value: getFormValue("usiNumber"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.usiNumber}
            touched={formik.touched.usiNumber}
          />
        </>
      ),
    },

    {
      id: "highschool",
      label: "High School",
      title: "Edit High School Education",
      onSave: saveHighSchool,
      onCancel: resetFormToUserData,
      saveDisabled: !isHighSchoolSectionComplete(),
      formFields: (
        <>
          <TextInput
            id="highSchoolName"
            label="School Name *"
            placeholder="Enter school name"
            getFieldProps={() => ({
              value: getFormValue("highSchoolName"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.highSchoolName}
            touched={formik.touched.highSchoolName}
          />
          <TextInput
            id="highSchoolCity"
            label="City *"
            placeholder="Enter city"
            getFieldProps={() => ({
              value: getFormValue("highSchoolCity"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.highSchoolCity}
            touched={formik.touched.highSchoolCity}
          />
          <GeneralSelectInput
            id="highSchoolCountry"
            label="Country *"
            options={countriesFlat}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.highSchoolCountry}
            error={formik.errors.highSchoolCountry}
            touched={formik.touched.highSchoolCountry}
          />
          <GeneralSelectInput
            id="highSchoolGrade"
            label="Grade *"
            options={gradeOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={getFormValue("highSchoolGrade") as string}
            error={formik.errors.highSchoolGrade}
            touched={formik.touched.highSchoolGrade}
          />
          <DatePickerInput
            id="highSchoolStartDate"
            className="highSchoolStartDate"
            label="Start Date"
            value={getFormValue("highSchoolStartDate") as Date | null}
            onChange={(date) =>
              formik.setFieldValue("highSchoolStartDate", date)
            }
            preset="pastDate"
          />
          <DatePickerInput
            id="highSchoolEndDate"
            className="highSchoolEndDate"
            label="End Date"
            value={getFormValue("highSchoolEndDate") as Date | null}
            disabled={getFormValue("highSchoolIsStillAttending") as boolean}
            onChange={(date) => formik.setFieldValue("highSchoolEndDate", date)}
            preset="pastDate"
          />
          <CheckBox
            label="Currently studying"
            checked={getFormValue("highSchoolIsStillAttending") as boolean}
            disabled={
              !isHighSchoolComplete() ||
              !!getFormValue("tertiary1IsStillAttending")
            }
            onChange={(checked) =>
              handleCurrentlyStudyingChange(
                "highSchoolIsStillAttending",
                checked
              )
            }
          />
        </>
      ),
    },
    ...(shouldShowTertiary1()
      ? [{ ...createTertiaryEditTab(1), onCancel: resetFormToUserData }]
      : []),
    ...(shouldShowTertiary2()
      ? [{ ...createTertiaryEditTab(2), onCancel: resetFormToUserData }]
      : []),
    ...(shouldShowTertiary3()
      ? [{ ...createTertiaryEditTab(3), onCancel: resetFormToUserData }]
      : []),
  ];

  return (
    <div className="form-group academic-history-page">
      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
      <TabbedFormToggle
        headerComponent={
          <div className="form-group header">
            <h2>Academic History</h2>
          </div>
        }
        displayTabs={displayTabs}
        editTabConfigs={editTabConfigs}
        formikInstance={formik}
        editingTab={editingTab}
        onTabEditToggle={setEditingTab}
      />
    </div>
  );
};

export default AcademicHistoryPage;
